import React, { useState } from 'react';
import { Instrument } from './InstrumentSelector';
import { Note } from 'tonal';
import { CustomTuning } from './TuningSelector';
import useLocalStorage from '../hooks/useLocalStorage';

interface CustomTuningFormProps {
  instrument: Instrument;
  onSave: (tuning: CustomTuning) => void;
  onCancel: () => void;
  initialTuning?: { name: string; notes: string[]; description: string };
}

const CustomTuningForm: React.FC<CustomTuningFormProps> = ({ instrument, onSave, onCancel, initialTuning }) => {
  const [name, setName] = useState((initialTuning?.name ? initialTuning.name + " (Copy)" : '') || '');
  const [notes, setNotes] = useState<string[]>(() => {
    // If we have an initial tuning, use its notes
    if (initialTuning?.notes) {
      return [...initialTuning.notes];
    }
    // Otherwise use the first tuning from the instrument
    return [...instrument.tunings[0].notes];
  });
  const [description, setDescription] = useState(initialTuning?.description || '');
  const [error, setError] = useState('');
  const [customTunings] = useLocalStorage<CustomTuning[]>('customTunings', []);

  // Reset form when initialTuning changes
  React.useEffect(() => {
    if (initialTuning) {
      setName(initialTuning.name + " (Copy)");
      setNotes([...initialTuning.notes]);
      setDescription(initialTuning.description);
    }
  }, [initialTuning]);

  const handleNameChange = (newName: string) => {
    setName(newName);
    setError('');
  };

  const handleNoteChange = (index: number, value: string) => {
    const newNotes = [...notes];
    newNotes[index] = value;
    setNotes(newNotes);
    setError(''); // Clear any existing errors when notes change
  };

  const validateNote = (note: string): boolean => {
    return Note.get(note).empty === false;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate name
    if (!name.trim()) {
      setError('Please enter a tuning name');
      return;
    }

    // Validate notes
    if (notes.some(note => !validateNote(note))) {
      setError('Invalid note format. Please use format like C4, F#3, Bb2');
      return;
    }

    // Check for name conflicts in both standard and custom tunings
    const nameExists = instrument.tunings.some(t => t.name === name) || 
                      customTunings.some(t => t.name === name && t.instrumentName === instrument.name);
    
    if (nameExists) {
      setError('This tuning name already exists. Please choose a different name.');
      return;
    }

    // Save the tuning
    onSave({
      name: name.trim(),
      notes: [...notes],  // Create a new array to avoid reference issues
      description: "",
      instrumentName: instrument.name
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <div className="form-control">
        <label className="label">
          <span className="label-text">Tuning Name:</span>
        </label>
        <input
          type="text"
          className="input input-bordered w-full"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="Enter tuning name"
        />
      </div>

      <div className="form-control">
        <label className="label">
          <span className="label-text">String Notes:</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {notes.map((note, index) => (
            <input
              key={index}
              type="text"
              className="input input-bordered w-16"
              value={note}
              onChange={(e) => handleNoteChange(index, e.target.value)}
              placeholder={`String ${index + 1}`}
              maxLength={3}
            />
          ))}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      <div className="modal-action">
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Save Tuning
        </button>
      </div>
    </form>
  );
};

export default CustomTuningForm; 